/* eslint-disable max-lines */
import { EMessageType, IChatV2Schema, EChatV2Status } from '@cian/chats-api';

import { openEventChannel } from './openEventChannel';
import { TFrameCompanion } from '../..//types/openedChats';
import { BLOB_ID } from '../../constants/blobId';
import { trackFileSend } from '../../containers/AttachFileButtonContainer/tracking';
import { trackUnarchivedButtonClick } from '../../containers/MessageInputFieldContainer/tracking';
import { checkUsersSession } from '../../services/checkUsersSession';
import { clearDeletedMessageId } from '../../slices/deleteMessageSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { EHostType } from '../../types/hostType';
import { EMessageFileTypes } from '../../types/messageFiles';
import { TMessageSchema } from '../../types/messages';
import { EMobileLayoutActiveSide } from '../../types/mobileSide';
import { TThunkAction } from '../../types/redux/thunk';
import { ERoute } from '../../types/route';
import { ETelemetryEvents } from '../../types/telemetry';
import { EChatRole } from '../../types/userData';
import { deepEqual } from '../../utils/deepEqual';
import { addIncomingMessage } from '../chat/actions';
import { updateChatImages } from '../chatImages';
import { toggleChangeListActive, updatedFromServer } from '../chatList';
import { removeFromArchive } from '../chatOperations';
import { openChat, closeChat } from '../chatWindow';
import { setWsSendSucceed } from '../files/actions';
import { setMobileActiveSide } from '../mobileSide';
import { addOpenedChat, deleteOpenedChat } from '../openedChats/actions';
import { changeRoute } from '../route';
import { setSendFirstMessageSuccess, setSendMessageSuccess } from '../sendMessage';
import { setUnreadMessagesCount } from '../unreadMessagesCount';
import { closeUserCard } from '../userCard/closeUserCard';
import { setUserStatus } from '../userStatus';
import { setWsError } from '../wsError';
import { setWsModalError } from '../wsError/setWsModalError';
import { IWsErrorEvent, IWsModalErrorEvent } from '../wsError/types';

export const listenEventChannel =
  (): TThunkAction<void> =>
  (dispatch, getState, { logger, telemetry, custom: { messagesMap, socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const onMessageId = eventChannel.addIncommingMessageListener(({ data }) => {
      const state = getState();

      const {
        chatList,
        chatWindow,
        hostType,
        user,
        openedChats,
        deleteMessage: { deletedMessageId },
      } = state;
      const { isChangeListActive, chats, currentRoute } = chatList;

      // tslint:disable-next-line:switch-default
      switch (data.type) {
        case 'session':
          // FIXME перенести в редюсер
          if (data.uid) {
            const clientEvent = clientEvents.get(data.uid);

            if (!clientEvent) {
              return;
            }

            clientEvents.set(data.uid, { ...clientEvent, status: ERequestApiStatus.Succeed });
          }

          /**
           * Событие сессии от сервера. Приходит в случаях:
           * 1. Когда мы регистрируем сессию по WS и нам приходит эхо события.
           * 2. Когда сессия истекла и сервер отправил событие.
           * 3. Сервер отправил событие ошибки с кодом sessionExpired.
           */
          if (data.session.expired) {
            telemetry.increment(ETelemetryEvents.ChatWsSessionExpire);
            logger.warning('Проверка сессии. Сессия истекла', {
              domain: 'src/shared/actions/socket/listenEventChannel.ts',
              message: 'Сессия истекла. Пришло сообщение по ws',
            });
            dispatch(checkUsersSession());
          }
          telemetry.increment(ETelemetryEvents.ChatWsSessionSuccess);

          break;
        case 'chat':
          // После добавления custom actions нужно дополнительно проверять, что изменился статус чата (заблокирован/удален/заархивирован)
          const currentChat = chats.find(chat => chat.chatId === data.chat.chatId);

          const chat = data.chat as unknown as IChatV2Schema;

          // TODO уберем когда и ручки чатов обновятся
          const isArchived = chat.status === EChatV2Status.Archived;
          const isBlocked = chat.status === EChatV2Status.Blocked;
          const isDeleted = chat.status === EChatV2Status.Deleted;

          const wasChangedChatCustomActions = currentChat
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              !deepEqual(currentChat.customActions as any, chat.customActions as any)
            : false;

          const wasChangedChatStatus =
            currentChat &&
            (currentChat.archived !== isArchived ||
              currentChat.blocked !== isBlocked ||
              chat.status === EChatV2Status.Deleted);

          dispatch(updatedFromServer(chat));

          // Если чатов нет, а изменение списка активно, то деактивировать изменение списка
          if (!chats.length && isChangeListActive) {
            dispatch(toggleChangeListActive());
          }
          if (wasChangedChatCustomActions && !wasChangedChatStatus && currentRoute === ERoute.Archive) {
            return;
          }

          // Если чат был открыт и его удалили или перенесли в архив
          if (chatWindow.currentOpenedChatId === chat.chatId && (isDeleted || isArchived)) {
            dispatch(closeUserCard());

            // Если чат не в iFrame, то закрыть чат и переключиться на список чатов
            if (hostType !== EHostType.Frame) {
              dispatch(closeChat());
              dispatch(setMobileActiveSide(EMobileLayoutActiveSide.Left));
            } else {
              // Если чат в iFrame, то переключить пользователя на BLOB_ID и удалить чат
              const agentUser = chat.users.find(user => user.chatRole === EChatRole.Opponent);

              const {
                offer,
                agentId,
                frameCompanion,
                editableMessage,
                defaultMessagePayload,
                sendDefaultMessage,
                hintQuestions: { hints, banner },
                ga: { source, products },
                availableDates: { checkin, checkout },
              } = openedChats[chatWindow.currentOpenedChatId];
              dispatch(
                addOpenedChat({
                  chatId: BLOB_ID,
                  offer,
                  newChat: {
                    agentId,
                    // TODO нужно исправить на бекенде для версии v2
                    frameCompanion: frameCompanion || {
                      ...agentUser,
                      userId: agentUser?.userId || 0,
                      name: agentUser?.name || '',
                      chatRole: agentUser?.chatRole as unknown as TFrameCompanion['chatRole'],
                      agentAccountType: agentUser?.agentAccountType as unknown as TFrameCompanion['agentAccountType'],
                      hasPublicProfile: agentUser?.hasPublicProfile || false,
                      urlToAgentPublicProfile: '',
                    },
                    defaultMessage: editableMessage,
                    defaultMessagePayload,
                    sendDefaultMessage,
                    hintQuestions: hints,
                    hintQuestionBanner: banner,
                    source,
                    gaProducts: products,
                    checkin,
                    checkout,
                    status: ERequestApiStatus.Succeed,
                  },
                }),
              );
              dispatch(openChat(BLOB_ID));
              dispatch(deleteOpenedChat(chatWindow.currentOpenedChatId));
            }

            return;
          }

          if (currentRoute === ERoute.Archive && chatWindow.currentOpenedChatId === chat.chatId) {
            dispatch(changeRoute({ route: ERoute.Messages, notClearCurrentOpenedChatId: true }));
          }

          if (!chat.lastMessage?.uid) {
            return;
          }

          const event = clientEvents.get(chat.lastMessage.uid);

          /**
           * При отсылке сообщения c веб от сервера приходит 2 сообщения: chat и message.
           * В случае, если это исходящее сообщение, сообщение chat нужно игнорировать,
           * так как статус выставляет сообщение message.
           * Однако, стоит помнить, что с сервера приходят сообщения от других участников и
           * их надо добавлять, как входящие. Также статус сообщения может быть изменен на Прочитано
           */
          if (!event || (event.type === EWsClientEventType.Message && event.status !== ERequestApiStatus.Pending)) {
            // если сообщение удалено, то не обрабатываем его
            if (deletedMessageId === chat.lastMessage.messageId) {
              return;
            }
            dispatch(addIncomingMessage({ message: chat.lastMessage as unknown as TMessageSchema, messagesMap }));

            // Если пришло сообщение с фото - обновить последние изображения чата
            if (chat.lastMessage.files?.images?.length) {
              dispatch(updateChatImages({ chatId: chat.chatId as string }));
            }
          }

          break;
        case 'message':
          const { uid, message } = data;
          const { files, chatId, type, userId: messageUserId, messageId } = message;

          // если сообщение удалено, то не обрабатываем его
          if (deletedMessageId === messageId) {
            dispatch(clearDeletedMessageId());

            return;
          }

          const { images = [], documents = [], imagesAsDocuments = [], fileTypes = [] } = files || {};
          const messageChat = chats.find(chat => chat.chatId === chatId);

          const currentOpenedChat = chatWindow.currentOpenedChatId === BLOB_ID ? openedChats[BLOB_ID] : undefined;

          const isNewChat =
            chatId &&
            user?.userId === messageUserId &&
            currentOpenedChat &&
            currentOpenedChat.firstMessageStatus === EFirstMessageStatus.Sent &&
            currentOpenedChat.newChatId === chatId;

          const opponent = messageChat?.users?.find(user => user.chatRole === EChatRole.Opponent);

          // При отправке первого исходящего сообщения по ручке обратно приходит вебсокет с этим сообщением
          if (isNewChat) {
            telemetry.increment(ETelemetryEvents.ChatWsFirstMessageGetSuccess);
            dispatch(setSendFirstMessageSuccess({ message: message as unknown as TMessageSchema, messagesMap }));
            dispatch(openChat((message as unknown as TMessageSchema).chatId));
            dispatch(deleteOpenedChat(BLOB_ID));

            return;
          }

          // Вебсокет message без data.uid приходит всегда, когда появляется новое сообщение.
          // Вебсокет message c data.uid приходит только отправителю, когда он отправил сообщение через WebSocket с этим uid.
          if (!uid) {
            return;
          }

          const clientEvent = clientEvents.get(uid);

          if (!clientEvent) {
            return;
          }

          clientEvents.set(uid, { ...clientEvent, status: ERequestApiStatus.Succeed });

          dispatch(
            setSendMessageSuccess({ message: message as unknown as TMessageSchema, messageId: uid, messagesMap }),
          );

          // Если была отправка сообщения с файлами (фото, документ, фотофайл)
          if (type === EMessageType.TextAndFiles) {
            const fileUid = images[0]?.uid || documents[0]?.uid || imagesAsDocuments[0]?.uid;
            const fileType = fileTypes[0] as EMessageFileTypes;

            dispatch(setWsSendSucceed({ chatId: chatId as string, uid }));
            trackFileSend({
              fileType,
              chatId: chatId as string,
              status: 'success',
              fileId: fileUid,
              opponentId: opponent?.userId,
            });
          }

          if (currentRoute === ERoute.Archive && chatId) {
            dispatch(removeFromArchive(chatId));
            trackUnarchivedButtonClick(opponent?.userId);
          }

          telemetry.increment(ETelemetryEvents.ChatWsMessageSendSuccess);

          break;
        case 'status':
          if (data.status) {
            dispatch(setUserStatus(data.status));
            telemetry.increment(ETelemetryEvents.ChatWsUserStatusSuccess);
          }
          break;
        case 'unread':
          dispatch(setUnreadMessagesCount(data.unread));

          break;
        case 'errorWithModal':
          const { modalMessage } = data as IWsModalErrorEvent;
          logger.warning('Получение модалки с ошибкой по ws', {
            domain: 'src/shared/actions/socket/listenEventChannel.ts',
            message: modalMessage.title || '',
          });
          // TODO: собрать кейсы, когда приходят события ошибки без uid'ов
          // В этом случае скорее всего нужно выкидывать исключение
          if (data.uid) {
            dispatch(setWsModalError(data as IWsModalErrorEvent));
          }
          break;
        case 'error':
          const { error } = data as IWsErrorEvent;
          logger.warning('Получение ошибок по ws', {
            domain: 'src/shared/actions/socket/listenEventChannel.ts',
            message: error?.message || '',
          });
          // TODO: собрать кейсы, когда приходят события ошибки без uid'ов
          // В этом случае скорее всего нужно выкидывать исключение
          if (data.uid) {
            dispatch(setWsError(data as IWsErrorEvent));
          }
      }
    });

    const onCloseId = eventChannel.addEventChannelListener('close', () => {
      eventChannel.removeIncommingMessageListener(onMessageId as string);
      eventChannel.removeEventChannelListener('close', onCloseId);

      telemetry.increment(ETelemetryEvents.ChatWsSocketClose);
      logger.warning('WS соединение закрыто', {
        domain: 'src/shared/services/chatbotFeedback/fetchOfferView/fetchOfferView.ts',
      });

      dispatch(openEventChannel());
    });
  };
